/* "What's New" changelog — the data.
 *
 * THIS is the only file you edit to add an announcement: append an entry to the
 * `announcements` array below (between the @announcements markers). No components or logic
 * live here. The CI drafter (.github/workflows/draft-changelog.yaml → scripts/draftChangelog.ts)
 * also edits only this file, inserting new entries after the @announcements:start marker.
 *
 * Each `title` is self-contained localized data ({ en, es, ... }); English is the source of
 * truth and the fallback for any language without its own translation. Keep the @announcements
 * markers intact.
 */

export type AnnouncementKind = "feature" | "fix";

/** Language code -> text. `en` is required — the source of truth and the fallback. */
export type LocalizedText = { en: string } & Record<string, string>;

export type Announcement = {
  id: string; // stable, unique (e.g. "2026-07-mm-sex-split")
  date: string; // ISO YYYY-MM-DD — merge/release date, used for sorting and the "new since" badge
  kind: AnnouncementKind;
  area: string; // short category label, e.g. "Reports", "Territories", "Meetings"
  title: LocalizedText; // one-line, user-facing summary, per language
};

/** The changelog entries (any order — the app sorts newest-first). */
export const announcements: Announcement[] = [
  /* @announcements:start — new entries are inserted below this line by scripts/draftChangelog.ts (keep this marker) */
  {
    id: "2026-11-hourglass-assistant",
    date: "2026-11-03",
    kind: "feature",
    area: "Assistant",
    title: {
      "en": "The new Hourglass Assistant answers your questions about using Hourglass, right from any page",
      "cs": "Nový Hourglass Assistant odpovídá na vaše otázky o používání aplikace Hourglass přímo z kterékoli stránky",
      "de": "Der neue Hourglass Assistant beantwortet Ihre Fragen zur Nutzung von Hourglass direkt auf jeder Seite",
      "el": "Ο νέος Hourglass Assistant απαντά στις ερωτήσεις σας για τη χρήση του Hourglass, απευθείας από οποιαδήποτε σελίδα",
      "es": "El nuevo Hourglass Assistant responde tus preguntas sobre el uso de Hourglass, directamente desde cualquier página",
      "fr": "Le nouvel Hourglass Assistant répond à vos questions sur l’utilisation d’Hourglass, directement depuis n’importe quelle page",
      "hu": "Az új Hourglass Assistant bármelyik oldalról megválaszolja az Hourglass használatával kapcsolatos kérdéseit",
      "id": "Hourglass Assistant yang baru menjawab pertanyaan Anda tentang cara menggunakan Hourglass, langsung dari halaman mana pun",
      "it": "Il nuovo Hourglass Assistant risponde alle tue domande sull'uso di Hourglass, direttamente da qualsiasi pagina",
      "ja": "新しいHourglass Assistantが、どのページからでもHourglassの使い方に関する質問に答えます",
      "ko": "새로운 Hourglass Assistant가 어느 페이지에서든 Hourglass 사용에 관한 질문에 바로 답해 드립니다",
      "nl": "De nieuwe Hourglass Assistant beantwoordt je vragen over het gebruik van Hourglass, direct vanaf elke pagina",
      "pl": "Nowy Hourglass Assistant odpowiada na pytania dotyczące korzystania z Hourglass, bezpośrednio z dowolnej strony",
      "pt-BR": "O novo Hourglass Assistant responde às suas perguntas sobre como usar o Hourglass, direto de qualquer página",
      "pt-PT": "O novo Hourglass Assistant responde às suas perguntas sobre como usar o Hourglass, diretamente a partir de qualquer página",
      "ro": "Noul Hourglass Assistant răspunde la întrebările tale despre utilizarea Hourglass, direct din orice pagină",
      "ru": "Новый Hourglass Assistant отвечает на ваши вопросы об использовании Hourglass прямо с любой страницы",
      "sw": "Hourglass Assistant mpya hujibu maswali yako kuhusu jinsi ya kutumia Hourglass, moja kwa moja kutoka ukurasa wowote",
      "th": "Hourglass Assistant ใหม่ตอบคำถามเกี่ยวกับการใช้ Hourglass ของคุณได้ทันทีจากทุกหน้า",
      "tl": "Sinasagot ng bagong Hourglass Assistant ang mga tanong mo tungkol sa paggamit ng Hourglass, direkta mula sa anumang pahina",
      "tr": "Yeni Hourglass Assistant, Hourglass’ın kullanımıyla ilgili sorularınızı doğrudan herhangi bir sayfadan yanıtlar",
      "uk": "Новий Hourglass Assistant відповідає на ваші запитання про використання Hourglass прямо з будь-якої сторінки",
      "vi": "Hourglass Assistant mới giải đáp các câu hỏi của bạn về cách dùng Hourglass, ngay từ bất kỳ trang nào",
      "zh": "全新的 Hourglass Assistant 可在任何页面直接回答您有关使用 Hourglass 的问题",
      "zh-Hant": "全新的 Hourglass Assistant 可在任何頁面直接回答您有關使用 Hourglass 的問題",
    },
  }
  /* @announcements:end (keep this marker) */
];
